import { ChevronDownIcon } from "assets/icons/ChevronDown";
import PingAnimation from "components/PingAnimation/PingAnimation";
import { orderStatusConstants } from "constants/order";
import { UserRole } from "constants/userRole";
import { useLoading } from "context/LoadingContext";
import { ToastContext } from "context/ToastContext";
import { useUserWithAccount } from "context/UserAccountProvider";
import { Button } from "primereact/button";
import { OverlayPanel } from "primereact/overlaypanel";
import { Tooltip } from "primereact/tooltip";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useUpdateDefaultAccountMutation } from "store/queries/users";
import { onStaffAccountChange, userSelector } from "store/slices/user";
import { IAccount } from "types/Account/Account";

import {
  AccountLabelStyled,
  AccountListStyled,
  AccountNameButtonStyled,
  AccountRowStyled,
  ChevronStyled,
  DefaultSwitchStyled,
  EmptyStateStyled,
  FooterStyled,
  PanelStyled,
  SearchIconStyled,
  SearchInputStyled,
  SectionLabelStyled,
  TriggerLabelStyled,
  TriggerStyled,
} from "./styled";

const HOSPITAL_VIEW_PATH = /^\/hospitals\/view\/([^/?#]+)/;
const DEFAULT_SWITCH_TARGET = "account-switcher-default";

export function AccountSwitcher(): JSX.Element | null {
  const panel = useRef<OverlayPanel>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [pendingDefault, setPendingDefault] = useState<string | null>(null);
  const [isSavingDefault, setIsSavingDefault] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const { accounts, userInfo, userRefetch } = useUserWithAccount();
  const { currentAccount } = useSelector(userSelector);
  const { setIsLoading } = useLoading();
  const { current: toastElement } = useContext(ToastContext);
  const [updateDefaultAccount] = useUpdateDefaultAccountMutation();

  const hospitalViewId = location.pathname.match(HOSPITAL_VIEW_PATH)?.[1];
  const isOrdersPage = location.pathname === "/orders";

  // SuperAdmins switch between every account; everyone else only their own.
  // Mirrors ViewHospitals and SwitchAccountModal.
  const availableAccounts = useMemo(() => {
    if (userInfo?.scope === UserRole.SUPER_ADMIN) {
      return accounts?.data || [];
    }
    return (
      accounts?.data.filter((account) =>
        userInfo?.accountIds.includes(account.id)
      ) || []
    );
  }, [accounts, userInfo]);

  // Always derived from the URL (falling back to the redux currentAccount),
  // never held in local state. This component lives in the Navbar and never
  // unmounts, so any cached selection would survive navigation and go stale —
  // the old dropdown got away with local state only because it remounted with
  // a fresh `defaultSelected` on every page change.
  const selectedAccount = useMemo(() => {
    const id =
      hospitalViewId || searchParams.get("accountId") || currentAccount;
    return accounts?.data.find((account) => account.id === id);
  }, [accounts, hospitalViewId, searchParams, currentAccount]);

  // Same reasoning as above: the old dropdown dropped an unsaved default toggle
  // when it unmounted on navigation. Nothing unmounts here, so clear it
  // explicitly whenever the account context changes.
  useEffect(() => {
    setPendingDefault(null);
  }, [selectedAccount?.id]);

  // startsWith, not includes — matches the old searchAccount filter.
  const visibleAccounts = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return availableAccounts;

    return availableAccounts.filter((account) =>
      account.name.toLowerCase().startsWith(term)
    );
  }, [availableAccounts, search]);

  const activeDefault = pendingDefault ?? userInfo?.defaultAccount;
  const hasUnsavedDefault =
    !!pendingDefault && pendingDefault !== userInfo?.defaultAccount;

  const pendingCount = (account: IAccount) =>
    account.orders.filter(
      (order) => order.status === orderStatusConstants.DRAFT
    ).length;

  const selectAccount = (account: IAccount) => {
    panel.current?.hide();
    dispatch(onStaffAccountChange({ currentAccount: account }));
    setIsLoading(true);

    setTimeout(() => {
      setIsLoading(false);
      navigate(
        isOrdersPage
          ? `/orders?accountId=${account.id}`
          : `/hospitals/view/${account.id}`
      );
    }, 1000);
  };

  const saveDefaultAccount = async () => {
    if (!userInfo?.id || !pendingDefault) return;

    setIsSavingDefault(true);
    try {
      await updateDefaultAccount({
        userId: userInfo.id,
        defaultAccount: pendingDefault,
      }).unwrap();

      panel.current?.hide();
      toastElement?.show({
        severity: "success",
        summary: "Success",
        detail: "Default hospital updated successfully",
      });
      userRefetch?.();
    } catch (error) {
      console.error(error);
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to update default hospital",
      });
    } finally {
      setIsSavingDefault(false);
    }
  };

  // Only on the two pages that carry an account context, and only when there is
  // something to switch between.
  if ((!hospitalViewId && !isOrdersPage) || availableAccounts.length <= 1) {
    return null;
  }

  return (
    <>
      <TriggerStyled
        type="button"
        $open={isOpen}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        onClick={(e) => panel.current?.toggle(e)}
      >
        <TriggerLabelStyled>
          {selectedAccount?.name || "Select hospital"}
        </TriggerLabelStyled>

        <ChevronStyled $open={isOpen} aria-hidden="true">
          <ChevronDownIcon />
        </ChevronStyled>
      </TriggerStyled>

      <PanelStyled
        ref={panel}
        onShow={() => setIsOpen(true)}
        onHide={() => {
          setIsOpen(false);
          setSearch("");
          // pendingDefault deliberately survives a close, as it did before:
          // toggling a default then reopening keeps the unsaved choice.
        }}
      >
        <SectionLabelStyled>Switch facility</SectionLabelStyled>

        <div className="relative mb-2">
          <SearchIconStyled className="pi pi-search" aria-hidden="true" />
          <SearchInputStyled
            value={search}
            aria-label="Search facility"
            placeholder="Search facility"
            onChange={(e: any) => setSearch(e.target.value)}
          />
        </div>

        <Tooltip
          target={`.${DEFAULT_SWITCH_TARGET}`}
          style={{ fontSize: "13px", width: "200px" }}
        />

        <AccountListStyled>
          {visibleAccounts.map((account) => {
            const isActive = account.id === selectedAccount?.id;
            const count = pendingCount(account);

            return (
              <AccountRowStyled key={account.id} $active={isActive}>
                <AccountNameButtonStyled
                  type="button"
                  $active={isActive}
                  onClick={() => selectAccount(account)}
                >
                  <AccountLabelStyled>{account.name}</AccountLabelStyled>
                  {count ? (
                    <PingAnimation count={count} pingType="brand" size="sm" />
                  ) : null}
                  {isActive ? (
                    <i
                      className="pi pi-check ml-auto"
                      style={{ fontSize: "14px" }}
                      aria-hidden="true"
                    />
                  ) : null}
                </AccountNameButtonStyled>

                <DefaultSwitchStyled
                  className={DEFAULT_SWITCH_TARGET}
                  data-pr-tooltip="Set as default hospital"
                  checked={account.id === activeDefault}
                  onChange={() => setPendingDefault(account.id)}
                />
              </AccountRowStyled>
            );
          })}

          {!visibleAccounts.length ? (
            <EmptyStateStyled>No hospitals match “{search}”.</EmptyStateStyled>
          ) : null}
        </AccountListStyled>

        {hasUnsavedDefault ? (
          <FooterStyled>
            <Button
              label="Change default hospital"
              className="p-button-sm"
              disabled={!userInfo?.id || !pendingDefault}
              loading={isSavingDefault}
              onClick={saveDefaultAccount}
            />
          </FooterStyled>
        ) : null}
      </PanelStyled>
    </>
  );
}
